import React, { useEffect, useState } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow, Divider, Chip } from '@mui/material';
import { ProductsState } from '../../Shared/Types/types';
import sdfLogo from '../../Assets/images/sdf-logo.png';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';

const BILLING_CHANNEL = 'billing-display';

const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const SummaryRow = ({ label, value, highlight = false, green = false }: { label: string; value: string; highlight?: boolean; green?: boolean }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
        <Typography sx={{ color: highlight ? '#1e293b' : '#64748b', fontSize: highlight ? 20 : 15, fontWeight: highlight ? 700 : 400 }}>
            {label}
        </Typography>
        <Typography sx={{ color: green ? '#16a34a' : highlight ? '#0f3460' : '#334155', fontSize: highlight ? 24 : 15, fontWeight: highlight ? 800 : 500 }}>
            {value}
        </Typography>
    </Box>
);

const CustomerDisplay = () => {
    const [billData, setBillData] = useState<ProductsState | null>(null);

    useEffect(() => {
        const channel = new BroadcastChannel(BILLING_CHANNEL);
        channel.onmessage = (e: MessageEvent<ProductsState>) => {
            setBillData(e.data);
        };
        return () => channel.close();
    }, []);

    const isEmpty = !billData || billData.selectedProducts.length === 0;
    const customer = billData?.customerDetails;
    const summary = billData?.saleSummary;

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f1f5f9', display: 'flex', flexDirection: 'column' }}>

            {/* Header — white background so logo is crisp */}
            <Box sx={{ backgroundColor: '#ffffff', px: 4, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderBottom: '3px solid #0f3460' }}>
                <img src={sdfLogo} alt="Logo" style={{ height: 56 }} />
                <Typography sx={{ color: '#0f3460', fontSize: 15, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase' }}>
                    Customer Display
                </Typography>
            </Box>

            {isEmpty ? (
                /* Idle / Welcome Screen */
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                    <Box sx={{ backgroundColor: '#ffffff', borderRadius: 4, px: 8, py: 6, textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.07)' }}>
                        <img src={sdfLogo} alt="Logo" style={{ height: 70, marginBottom: 24 }} />
                        <Typography sx={{ color: '#0f3460', fontSize: 40, fontWeight: 800, letterSpacing: 1 }}>
                            Welcome!
                        </Typography>
                        <Typography sx={{ color: '#94a3b8', fontSize: 18, mt: 1 }}>
                            Your bill is being prepared...
                        </Typography>
                    </Box>
                </Box>
            ) : (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2, gap: 2 }}>

                    {/* Customer info bar */}
                    <Box sx={{ backgroundColor: '#0f3460', borderRadius: 2, px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                        {customer?.contact_name_1 ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PersonOutlineIcon sx={{ color: '#93c5fd', fontSize: 20 }} />
                                <Typography sx={{ color: '#e2e8f0', fontSize: 15, fontWeight: 600 }}>
                                    {customer.contact_name_1}
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PersonOutlineIcon sx={{ color: '#93c5fd', fontSize: 20 }} />
                                <Typography sx={{ color: '#94a3b8', fontSize: 15 }}>Walk-in Customer</Typography>
                            </Box>
                        )}

                        {customer?.contact_no_1 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PhoneOutlinedIcon sx={{ color: '#93c5fd', fontSize: 18 }} />
                                <Typography sx={{ color: '#e2e8f0', fontSize: 15 }}>{customer.contact_no_1}</Typography>
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ReceiptOutlinedIcon sx={{ color: '#93c5fd', fontSize: 18 }} />
                            <Typography sx={{ color: '#e2e8f0', fontSize: 15 }}>
                                Bill <strong>{customer?.billNo || '—'}</strong>
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarTodayOutlinedIcon sx={{ color: '#93c5fd', fontSize: 16 }} />
                            <Typography sx={{ color: '#e2e8f0', fontSize: 15 }}>{today}</Typography>
                        </Box>
                    </Box>

                    {/* Main content: products + summary side by side */}
                    <Box sx={{ flex: 1, display: 'flex', gap: 2, minHeight: 0 }}>

                        {/* Products table */}
                        <Box sx={{ flex: 1, backgroundColor: '#ffffff', borderRadius: 2, overflow: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ backgroundColor: '#0f3460', color: '#fff', fontSize: 14, fontWeight: 700, width: 36 }}>#</TableCell>
                                        <TableCell sx={{ backgroundColor: '#0f3460', color: '#fff', fontSize: 14, fontWeight: 700 }}>Item</TableCell>
                                        <TableCell align="center" sx={{ backgroundColor: '#0f3460', color: '#fff', fontSize: 14, fontWeight: 700, width: 60 }}>Qty</TableCell>
                                        <TableCell align="right" sx={{ backgroundColor: '#0f3460', color: '#fff', fontSize: 14, fontWeight: 700, width: 100 }}>MRP</TableCell>
                                        <TableCell align="right" sx={{ backgroundColor: '#0f3460', color: '#fff', fontSize: 14, fontWeight: 700, width: 100 }}>Price</TableCell>
                                        <TableCell align="right" sx={{ backgroundColor: '#0f3460', color: '#fff', fontSize: 14, fontWeight: 700, width: 110 }}>Amount</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {billData.selectedProducts.map((product, index) => (
                                        <TableRow key={product.barCode} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f8fafc' }, '&:hover': { backgroundColor: '#eff6ff' } }}>
                                            <TableCell sx={{ color: '#94a3b8', fontSize: 14 }}>{index + 1}</TableCell>
                                            <TableCell sx={{ color: '#1e293b', fontSize: 14 }}>
                                                <strong>{product.prod_title}</strong>
                                                {(product.size || product.colour) && (
                                                    <Chip
                                                        label={[product.size, product.colour].filter(Boolean).join(' / ')}
                                                        size="small"
                                                        sx={{ ml: 1, fontSize: 11, height: 20, backgroundColor: '#e2e8f0', color: '#475569' }}
                                                    />
                                                )}
                                                {product.disc_amt > 0 && (
                                                    <Chip
                                                        label={`-₹${product.disc_amt.toFixed(0)}`}
                                                        size="small"
                                                        sx={{ ml: 1, fontSize: 11, height: 20, backgroundColor: '#dcfce7', color: '#16a34a' }}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell align="center" sx={{ color: '#1e293b', fontSize: 15, fontWeight: 600 }}>{product.quantity}</TableCell>
                                            <TableCell align="right" sx={{ color: '#94a3b8', fontSize: 13, textDecoration: product.mrp > product.unit_price ? 'line-through' : 'none' }}>
                                                ₹{product.mrp.toFixed(2)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: '#334155', fontSize: 14 }}>
                                                ₹{product.unit_price.toFixed(2)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: '#0f3460', fontSize: 15, fontWeight: 700 }}>
                                                ₹{product.net_total.toFixed(2)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>

                        {/* Summary panel */}
                        <Box sx={{ width: 300, display: 'flex', flexDirection: 'column', gap: 2 }}>

                            <Box sx={{ backgroundColor: '#ffffff', borderRadius: 2, p: 2.5, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                                <Typography sx={{ color: '#94a3b8', fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', mb: 1.5 }}>
                                    Bill Summary
                                </Typography>

                                <SummaryRow label="Items" value={`${summary?.total_items ?? 0}`} />
                                <SummaryRow label="MRP Total" value={`₹${summary?.MRP.toFixed(2) ?? '0.00'}`} />
                                <SummaryRow label="Subtotal" value={`₹${summary?.subTotal.toFixed(2) ?? '0.00'}`} />

                                {(summary?.discount ?? 0) > 0 && (
                                    <SummaryRow label="Discount" value={`- ₹${summary!.discount.toFixed(2)}`} green />
                                )}


                                {(summary?.roundOffVal ?? 0) !== 0 && (
                                    <SummaryRow label="Round Off" value={`₹${summary!.roundOffVal.toFixed(2)}`} />
                                )}

                                <Divider sx={{ my: 1.5 }} />

                                <SummaryRow
                                    label="Total"
                                    value={`₹${Math.round(summary?.netTotal ?? 0)}`}
                                    highlight
                                />
                            </Box>

                            {(summary?.MRPDiscValue ?? 0) > 0 && (
                                <Box sx={{ backgroundColor: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 2, p: 2, textAlign: 'center' }}>
                                    <Typography sx={{ color: '#15803d', fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        You saved
                                    </Typography>
                                    <Typography sx={{ color: '#16a34a', fontSize: 28, fontWeight: 800 }}>
                                        ₹{summary!.MRPDiscValue.toFixed(2)}
                                    </Typography>
                                    <Typography sx={{ color: '#86efac', fontSize: 12 }}>on this purchase</Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* Footer */}
                    <Box sx={{ textAlign: 'center', py: 0.5 }}>
                        <Typography sx={{ color: '#94a3b8', fontSize: 13 }}>
                            Thank you for shopping with us
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default CustomerDisplay;
